'use client';

import { matchesPublicCategoryProductsSeed, type PublicCategoryProductsSeed } from '@eliadmin/core/src/components/categories/public-category-products-seed';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { buildProductsRequest } from '@eliadmin/core/src/api/products/product-list-request';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { ProductsGrid } from '@eliadmin/core/src/components/product/products-grid';
import { ProductCardHeadingContext } from '../product/product-card/heading-context';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalNavigation, useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQueryClient, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { CollectionPageSchema } from '@eliadmin/core/src/components/seo/product-list-schema';

export type CategoryProps = {
  initialProducts?: PublicCategoryProductsSeed;
  className?: {
    grid_wrapper?: string;
    product?: string;
  };
  category: Category;
  onProductTotalChange?: (total: number) => void;
  paginationProps?: { scrollToElementAfterPageChange?: string };
};

export function CategoryProducts(props: CategoryProps) {
  const isInitialized = useCustomerStore(state => state.isInitialized);

  if (!isInitialized && !props.initialProducts) return <CategoryProductsSkeleton />;

  return <CategoryProductsContent {...props} />;
}

function CategoryProductsContent(props: CategoryProps) {
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const filters = useCategoryStore(s => s.filter);
  const searchParams = useSearchParams();
  const queryClient = useQueryClient();

  const currentPage = searchParams.get('page');

  const { t } = useTranslations();

  const generalLayout = useGlobalStore(s => s.generalLayout);

  const queryWithoutPage = buildProductsQuery({
    attributes: filters.attributes,
    categories: [props.category.id],
    manufacturers: filters.manufacturers ?? [],
    results: generalLayout?.pagination?.paginationProducts || 12,
    order: filters.sort.order,
    orderBy: filters.sort.orderBy,
    // values: filters.attributes.flatMap(attr => attr.values),
    price_from: filters.price_min,
    price_to: filters.price_max,
    availabilities: filters.availabilities,
    suppliers: filters.suppliers,
    showAttributes: true,
  });

  const getProductQuery = (page?: number) => {
    const query: typeof queryWithoutPage & Record<string, any> = {
      ...queryWithoutPage,
      page,
    };

    return query;
  };
  const productRequestUri = buildProductsRequest(
    getProductQuery(currentPage ? parseInt(currentPage) : 1),
  ).uri;
  const productResetUri = buildProductsRequest(getProductQuery()).uri;

  const productQueryKey = [
    CacheKey.PRODUCTS,
    'infinite',
    locale,
    authToken ?? null,
    currentPage,
    productRequestUri,
    filters.attributes,
  ];

  const productsQuery = useSuspenseInfiniteQuery({
    initialData: matchesPublicCategoryProductsSeed(props.initialProducts, {
      locale: locale, requestUri: productRequestUri, authToken, page: Number(currentPage ?? 1),
    }) ? { pages: [props.initialProducts!.data], pageParams: [Number(currentPage ?? 1)] } : undefined,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    initialDataUpdatedAt: props.initialProducts?.updatedAt,
    queryKey: productQueryKey,
    queryFn: ({ pageParam, signal }) => {
      return fetchProducts({
        query: getProductQuery(pageParam),
        locale,
        authToken,
        signal,
      });
    },
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: currentPage ? parseInt(currentPage) : 1,
  });

  const isGlobalNavigating = useGlobalNavigation();

  const lastPage = productsQuery.data?.pages[productsQuery.data?.pages.length - 1];
  const flatList = productsQuery.data?.pages.flatMap(page => page.data) ?? [];

  useEffect(() => {
    props.onProductTotalChange?.(lastPage?.meta?.total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage?.meta?.total]);

  return (
    <div
      className={cn(
        'relative col-span-12 flex w-full flex-col gap-6 @container',
        'ea-category-products',
        {
          'col-span-12': !templateSettings.category?.left_filter?.enabled,
        },
      )}
    >
      {!flatList?.length && <p className="text-center text-muted-foreground">{t('text554')}</p>}
      {!!flatList.length && (
        <CollectionPageSchema
          category={props.category}
          products={flatList}
          order={filters.sort.order ?? 'desc'}
          url={props.category.breadcrumb?.slug || props.category.seo?.['og:url']}
          currency={(generalLayout?.currency_code || generalLayout?.currency)}
        />
      )}
      <ProductCardHeadingContext.Provider value="h2">
        <ProductsGrid
          products={flatList ?? []}
          className={{
            wrapper: cn(
              'grid grid-cols-2  @lg:grid-cols-2 @2xl:grid-cols-3',
              'ea-category-products__grid',
              props.className?.grid_wrapper,
            ),
            product: {
              main: cn(props.className?.product),
            },
          }}
        />
      </ProductCardHeadingContext.Provider>

      {!!productsQuery.hasNextPage && (
        <div
          className={cn('flex w-full flex-row justify-center', 'ea-category-products__load-more')}
        >
          <Button
            variant="secondary"
            isLoading={productsQuery.isFetchingNextPage}
            aria-label={t('text338')}
            onClick={() => productsQuery.fetchNextPage()}
          >
            {t('text338')}
          </Button>
        </div>
      )}

      <div
        className={cn('flex w-full flex-row justify-center', 'ea-category-products__pagination')}
      >
        <Pagination
          links={lastPage?.meta.links}
          onPageClicked={page => {
            if (String(page) !== String(currentPage)) return;
            queryClient.resetQueries({
              queryKey: productQueryKey,
              exact: true,
            });
          }}
          scrollToElementAfterPageChange={
            props.paginationProps?.scrollToElementAfterPageChange || undefined
          }
          resetWhen={[productResetUri]}
        />
      </div>

      {!!isGlobalNavigating && (
        <div
          className={cn(
            'absolute inset-0 z-10 bg-background/30',
            'ea-category-products__loading-overlay',
          )}
        ></div>
      )}
    </div>
  );
}

type CategoryProductsSkeletonProps = {
  className?: {
    container?: string;
    items_wrapper?: string;
    item?: string;
  };
  numberOfSkeletons?: number;
};

export function CategoryProductsSkeleton({
  className,
  numberOfSkeletons,
}: CategoryProductsSkeletonProps) {
  const templateSettings = useGlobalStore(s => s.templateSettings);

  const array = Array.from({ length: numberOfSkeletons || 6 }, (_, i) => i);

  return (
    <div
      className={cn(
        'relative col-span-12 flex w-full flex-col gap-4 @container',
        'ea-category-products',
        {
          'col-span-12': !templateSettings.category?.left_filter?.enabled,
        },
        className?.container,
      )}
    >
      <div
        className={cn(
          'grid w-full gap-4 @sm:grid-cols-1 @lg:grid-cols-2 @2xl:grid-cols-3',
          className?.items_wrapper,
        )}
      >
        {array.map(i => (
          <Skeleton key={i} className={cn('min-h-[400px] min-w-[250px]', className?.item)} />
        ))}
      </div>

      <Skeleton className="h-20 w-full" />
    </div>
  );
}
